import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Mic, Clock, CheckCircle2, XCircle, Calendar, MessageCircleQuestion, Loader2, Trash2, Mail, FileText,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { AdminModal } from "@/components/AdminModal";
import { Header } from "./Schools";
import { CertificateCanvasPreview } from "./CertificateDesigner";

const STATUS_META = {
  new: { label: "New", cls: "bg-gray-100 text-gray-700" },
  under_review: { label: "Under Review", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700" },
  scheduled: { label: "Scheduled", cls: "bg-indigo-100 text-indigo-700" },
  recorded: { label: "Recorded", cls: "bg-sky-100 text-sky-700" },
  published: { label: "Published", cls: "bg-purple-100 text-purple-700" },
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", cls: "bg-rose-100 text-rose-700" },
  changes_requested: { label: "Changes requested", cls: "bg-amber-100 text-amber-700" },
};

const STATUS_FLOW = ["new", "under_review", "approved", "scheduled", "recorded", "published", "completed"];

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.new;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>{m.label}</span>;
}

export default function AdminPodcastBookings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [changesNote, setChangesNote] = useState("");
  const [showChanges, setShowChanges] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = statusFilter === "all" ? {} : { status: statusFilter };
    api.get("/podcast-bookings", { params })
      .then((r) => setItems(r.data || []))
      .catch(() => { toast.error("Could not load podcast bookings"); setItems([]); })
      .finally(() => setLoading(false));
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status, extra = {}) => {
    setBusy(true);
    try {
      await api.patch(`/podcast-bookings/${id}`, { status, ...extra });
      toast.success(`Marked as ${STATUS_META[status]?.label || status}`);
      load();
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
    } catch { toast.error("Could not update status"); }
    finally { setBusy(false); }
  };

  const saveField = async (id, field, value) => {
    setBusy(true);
    try {
      await api.patch(`/podcast-bookings/${id}`, { [field]: value });
      toast.success("Saved");
      load();
    } catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this podcast booking request? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/podcast-bookings/${id}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch { toast.error("Failed to delete"); }
    finally { setBusy(false); }
  };

  const nextStatus = (status) => {
    const idx = STATUS_FLOW.indexOf(status);
    return idx >= 0 && idx < STATUS_FLOW.length - 1 ? STATUS_FLOW[idx + 1] : null;
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <Header title="Podcast Bookings" subtitle={`${items.length} request${items.length === 1 ? "" : "s"}`} />

      <div className="mt-6 flex flex-wrap gap-2">
        {["all", ...Object.keys(STATUS_META)].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all ${statusFilter === s ? "bg-[#0F172A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
          >
            {s === "all" ? "All" : STATUS_META[s].label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-6 space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center">
          <Mic className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <div className="font-display text-lg font-medium text-[#0F172A]">No Podcast Requests Yet</div>
          <p className="mt-1 text-sm text-gray-500">Podcast bookings from schools will appear here.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {items.map((b) => (
            <div key={b.id} className="rounded-xl border border-gray-200 bg-white p-4" data-testid={`pb-row-${b.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-[#0F172A]">{b.full_name}</h3>
                    <StatusBadge status={b.status} />
                  </div>
                  <div className="mt-1 text-xs text-gray-500 flex flex-wrap gap-x-3">
                    <span>{b.designation} · {b.school_name}</span>
                    <span>{b.preferred_topic}</span>
                    {b.preferred_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {b.preferred_date}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => setSelected(b)} className="text-xs font-medium bg-[#EEF2FF] text-[#4F46E5] px-3 py-1.5 rounded-lg hover:bg-indigo-100">
                    Review
                  </button>
                  <button onClick={() => remove(b.id)} disabled={busy} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-50 text-gray-500 hover:text-rose-600 disabled:opacity-40">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail / review modal */}
      {selected && (
        <AdminModal
          onClose={() => setSelected(null)}
          testId="pb-detail"
          maxWidth="max-w-2xl"
          title={<>{selected.full_name} <StatusBadge status={selected.status} /></>}
          subtitle={`${selected.designation} · ${selected.school_name} · ${selected.district || ""} ${selected.state || ""}`}
          footer={
            <div className="flex flex-wrap gap-2 w-full">
              {nextStatus(selected.status) && (
                <button
                  onClick={() => updateStatus(selected.id, nextStatus(selected.status))}
                  disabled={busy}
                  data-testid="pb-advance"
                  className="text-xs font-medium bg-emerald-600 text-white px-3 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Mark as {STATUS_META[nextStatus(selected.status)].label}
                </button>
              )}
              {selected.status !== "rejected" && (
                <button onClick={() => setShowChanges(true)} className="text-xs font-medium bg-amber-100 text-amber-700 px-3 py-2 rounded-lg hover:bg-amber-200 inline-flex items-center gap-1">
                  <MessageCircleQuestion className="w-3.5 h-3.5" /> Request Changes
                </button>
              )}
              {selected.status !== "rejected" && (
                <button onClick={() => setShowReject(true)} className="text-xs font-medium bg-rose-100 text-rose-700 px-3 py-2 rounded-lg hover:bg-rose-200 inline-flex items-center gap-1">
                  <XCircle className="w-3.5 h-3.5" /> Reject
                </button>
              )}
              <button
                onClick={() => toast.promise(api.patch(`/podcast-bookings/${selected.id}`, { status: selected.status }), { loading: "Resending…", success: "Email re-sent", error: "Failed" })}
                className="text-xs font-medium border border-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 inline-flex items-center gap-1"
              >
                <Mail className="w-3.5 h-3.5" /> Email Applicant
              </button>
            </div>
          }
        >
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <Info label="Email" value={selected.email} />
              <Info label="Mobile" value={selected.mobile} />
              <Info label="Topic" value={selected.preferred_topic} />
              <Info label="Podcast title" value={selected.podcast_title || "—"} />
              <Info label="Experience" value={selected.experience || "—"} />
              <Info label="Language" value={selected.preferred_language} />
              <Info label="Mode" value={selected.preferred_mode} />
              <Info label="Preferred date/time" value={`${selected.preferred_date || "—"} ${selected.preferred_time || ""}`} />
            </div>
            {selected.description && <Info label="Why feature me" value={selected.description} full />}
            {selected.special_requirements && <Info label="Special requirements" value={selected.special_requirements} full />}

            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${selected.consent_media ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                {selected.consent_media ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} Media consent
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${selected.consent_school_permission ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                {selected.consent_school_permission ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} School permission
              </span>
              {selected.permission_letter_url ? (
                <a href={selected.permission_letter_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-50 text-indigo-700">
                  <FileText className="w-3 h-3" /> Permission letter
                </a>
              ) : selected.permission_later ? (
                <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700">Will submit letter later</span>
              ) : null}
            </div>

            <div className="mt-5 grid gap-3">
              <label className="block">
                <span className="text-xs font-medium text-gray-600 mb-1 block">Coordinator</span>
                <input
                  defaultValue={selected.coordinator || ""}
                  onBlur={(e) => saveField(selected.id, "coordinator", e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm"
                  placeholder="Assign a coordinator"
                />
              </label>
              {(selected.status === "approved" || selected.status === "scheduled") && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600 mb-1 block">Meeting link</span>
                    <input
                      defaultValue={selected.meeting_link || ""}
                      onBlur={(e) => saveField(selected.id, "meeting_link", e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm"
                      placeholder="https://meet…"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600 mb-1 block">Notes</span>
                    <input
                      defaultValue={selected.admin_notes || ""}
                      onBlur={(e) => saveField(selected.id, "admin_notes", e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm"
                    />
                  </label>
                </div>
              )}
              {(selected.status === "approved" || selected.status === "scheduled" || selected.status === "recorded" || selected.status === "published" || selected.status === "completed") && (
                <PodcastCertificateSection booking={selected} />
              )}
            </div>
        </AdminModal>
      )}

      {/* Reject modal */}
      {showReject && (
        <AdminModal
          onClose={() => setShowReject(false)}
          title="Reject request"
          testId="pb-reject-modal"
          footer={
            <>
              <button onClick={() => setShowReject(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg w-full sm:w-auto">Cancel</button>
              <button
                onClick={async () => { await updateStatus(selected.id, "rejected", { admin_notes: rejectReason }); setShowReject(false); setRejectReason(""); }}
                className="px-4 py-2 text-sm font-medium bg-rose-600 text-white rounded-lg hover:bg-rose-700 w-full sm:w-auto"
              >
                Reject
              </button>
            </>
          }
        >
          <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason (shared with the applicant)" rows={3} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm resize-none" />
        </AdminModal>
      )}

      {/* Request changes modal */}
      {showChanges && (
        <AdminModal
          onClose={() => setShowChanges(false)}
          title="Request changes"
          testId="pb-changes-modal"
          footer={
            <>
              <button onClick={() => setShowChanges(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg w-full sm:w-auto">Cancel</button>
              <button
                onClick={async () => { await updateStatus(selected.id, "changes_requested", { admin_notes: changesNote }); setShowChanges(false); setChangesNote(""); }}
                className="px-4 py-2 text-sm font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600 w-full sm:w-auto"
              >
                Send request
              </button>
            </>
          }
        >
          <textarea value={changesNote} onChange={(e) => setChangesNote(e.target.value)} placeholder="What should the applicant update?" rows={3} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm resize-none" />
        </AdminModal>
      )}
    </motion.div>
  );
}

const Info = ({ label, value, full }) => (
  <div className={full ? "sm:col-span-2 mt-3" : ""}>
    <div className="text-xs text-gray-500">{label}</div>
    <div className="font-medium text-[#0F172A]">{value}</div>
  </div>
);

// ── Podcast Guest Certificate — Preview → Approve workflow ──────────────────────
// Lives inside the booking detail modal. Preview never touches the database (pure
// render via /certificate/preview). Approve is the ONLY action that creates a
// certificate record, assigns a number, generates the final PDF, and emails the
// guest (/certificate/approve) — reusing the same field-resolution logic as
// preview so what the admin previewed is exactly what gets approved.
function PodcastCertificateSection({ booking }) {
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null); // { template, values } — HTML preview only, never a PDF
  const [approving, setApproving] = useState(false);
  const [resending, setResending] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [form, setForm] = useState({
    guest_name: booking.full_name || "",
    podcast_series: booking.podcast_title || "",
    podcast_title: booking.podcast_title || "",
    episode_title: booking.podcast_title || booking.preferred_topic || "",
    episode_number: "",
    episode_theme: booking.preferred_topic || "",
    podcast_topic: booking.preferred_topic || "",
    podcast_language: booking.preferred_language || "",
    podcast_mode: booking.preferred_mode || "",
    recording_date: booking.preferred_date || "",
    certificate_number: "", // optional manual override — blank = auto-generated
  });

  const loadCert = useCallback(() => {
    setLoading(true);
    api.get(`/podcast-bookings/${booking.id}/certificate`).then(({ data }) => {
      setCert(data.certificate || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [booking.id]);

  useEffect(() => { loadCert(); }, [loadCert]);

  // Preview stays HTML-only — this never generates or downloads a PDF. It just
  // fetches the resolved field values + active template as JSON and renders them
  // with the same CertificateCanvasPreview component the Designer itself uses.
  const preview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post(`/podcast-bookings/${booking.id}/certificate/preview`, form);
      setPreviewData(data);
    } catch {
      toast.error("Could not load preview");
    }
    setPreviewing(false);
  };

  const approve = async () => {
    if (!window.confirm("Approve this certificate? This will assign a certificate number, generate the final PDF, and email the guest — this cannot be undone.")) return;
    setApproving(true);
    try {
      const { data } = await api.post(`/podcast-bookings/${booking.id}/certificate/approve`, form);
      setCert(data.certificate);
      toast.success(data.already ? "Certificate already approved" : "Certificate approved and emailed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not approve certificate");
    }
    setApproving(false);
  };

  const resend = async () => {
    if (!cert) return;
    setResending(true);
    try {
      await api.post(`/certificates/${cert.certificate_id}/email`);
      toast.success("Certificate re-sent");
    } catch {
      toast.error("Could not resend email");
    }
    setResending(false);
  };

  if (loading) {
    return <div className="mt-2 text-xs text-gray-400">Checking certificate status…</div>;
  }

  // Status badge — matches the four states named in the brief
  const statusBadge = cert
    ? (cert.emailed
        ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full"><CheckCircle2 className="w-3.5 h-3.5" /> Certificate Sent</span>
        : <span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-full"><CheckCircle2 className="w-3.5 h-3.5" /> Approved</span>)
    : previewData
      ? <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full"><FileText className="w-3.5 h-3.5" /> Preview Generated</span>
      : <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full"><Clock className="w-3.5 h-3.5" /> Pending Review</span>;

  return (
    <div className="mt-2 rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Podcast Guest Certificate</div>
        {statusBadge}
      </div>

      {cert ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-gray-500 font-mono">{cert.certificate_id}</span>
          <a
            href={`${BACKEND}/api/certificates/${cert.certificate_id}/pdf`}
            target="_blank" rel="noreferrer"
            className="text-xs font-medium border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-50 inline-flex items-center gap-1"
          >
            <FileText className="w-3.5 h-3.5" /> Download Certificate
          </a>
          <button
            onClick={resend}
            disabled={resending}
            className="text-xs font-medium border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-50"
          >
            {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Resend Email
          </button>
        </div>
      ) : (
        <>
          {/* Editable fields — available right up until approval */}
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-600 mb-1 block">Guest Name</span>
              <input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 mb-1 block">Podcast Series</span>
              <input value={form.podcast_series} onChange={(e) => setForm({ ...form, podcast_series: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm" placeholder="Manthan Foundation Podcast" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 mb-1 block">Podcast Title</span>
              <input value={form.podcast_title} onChange={(e) => setForm({ ...form, podcast_title: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm"
                placeholder={booking.podcast_title || "From guest's application"} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 mb-1 block">Episode Title</span>
              <input value={form.episode_title} onChange={(e) => setForm({ ...form, episode_title: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 mb-1 block">Episode No.</span>
              <input value={form.episode_number} onChange={(e) => setForm({ ...form, episode_number: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm" placeholder="12" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 mb-1 block">Theme / Topic</span>
              <input value={form.episode_theme} onChange={(e) => setForm({ ...form, episode_theme: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 mb-1 block">Recording Date</span>
              <input value={form.recording_date} onChange={(e) => setForm({ ...form, recording_date: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm" placeholder={booking.preferred_date || ""} />
            </label>
          </div>

          <button type="button" onClick={() => setShowAdvanced((v) => !v)} className="text-xs text-gray-500 hover:text-gray-700 mb-3">
            {showAdvanced ? "Hide" : "Show"} advanced fields
          </button>
          {showAdvanced && (
            <div className="grid sm:grid-cols-3 gap-3 mb-3">
              <label className="block">
                <span className="text-xs font-medium text-gray-600 mb-1 block">Language</span>
                <input value={form.podcast_language} onChange={(e) => setForm({ ...form, podcast_language: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm" />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 mb-1 block">Mode</span>
                <input value={form.podcast_mode} onChange={(e) => setForm({ ...form, podcast_mode: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm" />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 mb-1 block">Certificate No. (optional)</span>
                <input value={form.certificate_number} onChange={(e) => setForm({ ...form, certificate_number: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm" placeholder="Leave blank to auto-generate" />
              </label>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              onClick={preview}
              disabled={previewing}
              data-testid="pb-preview-certificate"
              className="text-xs font-medium border border-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              Preview Certificate
            </button>
            <button
              onClick={approve}
              disabled={approving || !form.guest_name}
              data-testid="pb-approve-certificate"
              title={!form.guest_name ? "Guest name is required" : undefined}
              className="text-xs font-medium bg-[#0F172A] text-white px-3 py-2 rounded-lg hover:bg-[#1e293b] disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {approving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Approve Certificate
            </button>
          </div>
          <p className="text-[11px] text-gray-400 mt-2">
            Preview is a live HTML render only — no PDF is generated and nothing is saved or emailed.
            Approve is final: it assigns a certificate number, generates the PDF, and emails the guest immediately.
          </p>

          {/* HTML-only preview — same positioning engine as the Designer canvas and the final PDF */}
          {previewData && (
            <div className="mt-4 rounded-xl border border-gray-200 bg-[#f0f0f0] p-4 overflow-auto">
              <CertificateCanvasPreview template={previewData.template} values={previewData.values} maxWidth={640} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
